
const SDK_TIMEOUT = 15000;

export class GoogleSDK {
  constructor() {
    this.auth2 = null;
    this.loading = null;
  }

  // Microsoft and email/password sessions must not depend on Google's SDK.
  load() {
    if (!this.loading) {
      this.loading = this.initialize().catch(error => {
        this.loading = null;
        throw error;
      });
    }
    return this.loading;
  }

  loadScript() {
    if (window.gapi?.load) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const existing = document.getElementById('google-jssdk');
      const script = existing || document.createElement('script');
      const finish = error => {
        clearTimeout(timer);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
        if (error) { script.remove(); reject(error); } else resolve();
      };
      const onLoad = () => finish(window.gapi?.load ? null : new Error('Google Sign-In SDK is unavailable.'));
      const onError = () => finish(new Error('Unable to load Google Sign-In. Please try again.'));
      const timer = setTimeout(() => finish(new Error('Google Sign-In loading timed out. Please try again.')), SDK_TIMEOUT);
      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);
      if (!existing) {
        script.id = 'google-jssdk';
        script.async = true;
        script.src = 'https://apis.google.com/js/platform.js';
        document.head.appendChild(script);
      }
    });
  }

  async initialize() {
    await this.loadScript();
    await new Promise((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => fail(new Error('Google Sign-In initialization timed out. Please try again.')), SDK_TIMEOUT);
      const fail = error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error);
      };
      try {
        window.gapi.load('auth2', {
          callback: () => {
            if (settled) return;
            try {
              const auth2 = window.gapi.auth2.init({
                client_id: process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID,
                cookie_policy: 'single_host_origin',
                scope: 'profile email openid'
              });
              auth2.then(() => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                this.auth2 = auth2;
                // GoogleAuth resolves with itself: resolving our promise with it
                // would recursively assimilate its then() method forever.
                resolve();
              }, fail);
            } catch (error) { fail(error); }
          },
          onerror: () => fail(new Error('Unable to initialize Google Sign-In. Please try again.'))
        });
      } catch (error) { fail(error); }
    });
  }

  async signIn() {
    await this.load();
    return this.auth2.signIn();
  }

  async signOut() {
    await this.load();
    return this.auth2.signOut();
  }

  async getIdToken() {
    await this.load();
    return this.auth2.currentUser.get().getAuthResponse().id_token;
  }

  errorMessage(error) {
    switch (error?.error) {
      case 'idpiframe_initialization_failed':
        return "Google Auth initialization failed.";
      case 'popup_closed_by_user':
        return "User cancelled the flow.";
      case 'access_denied':
        return "User denied required permissions.";
      case 'immediate_failed':
        return "Unable to autoselect user.";
      default:
        return error?.details || error?.message || "Unidentified failure.";
    }
  }
}

export const gapiInstance = new GoogleSDK();
